import { FormControl } from '@angular/forms';
import { map, ReplaySubject, Subject, takeUntil } from 'rxjs';
import {
  BdUiMultiSelectConfig,
  BdUiSelectLabelPluralization,
} from './multi-select.config';

export abstract class BdAbstractMultiSelect<
  TOption,
  TWrapperOption,
  TAllOptions extends Array<unknown> = Array<TWrapperOption>,
> {
  public valid = true;
  public searching = true;
  public selectCtrl: FormControl<TWrapperOption[]> = new FormControl<
    TWrapperOption[]
  >(this.emptySelection());

  public placeholderLabel: string;
  public noEntriesFoundLabel: string;
  public selectedItemsLabel: string;
  public errorMessage: string;
  public allOptions: Subject<TAllOptions> = new ReplaySubject<TAllOptions>(1);
  protected _onDestroy = new Subject<void>();
  private _selectLabelConfig: string | BdUiSelectLabelPluralization;

  protected init(config: BdUiMultiSelectConfig<TOption>): void {
    const displayLabel = this._getDisplayLabel(config.selectLabel);
    this.placeholderLabel = `${displayLabel} suchen`;
    this.noEntriesFoundLabel = `Keine ${displayLabel} gefunden`;
    this._selectLabelConfig = config.selectLabel;
    this.selectedItemsLabel = `{0} ${this._getLabelForCount(0)} ausgewählt`;

    this.selectCtrl.valueChanges
      .pipe(
        takeUntil(this._onDestroy),
        map((selection) => (selection ? selection : this.emptySelection())),
      )
      .subscribe((selection) => {
        this._updateSelectedItemsLabel(selection.length);
        this.onSelectionChange(selection);
      });

    if (config.valid$) {
      config.valid$.pipe(takeUntil(this._onDestroy)).subscribe((valid) => {
        this.valid = valid.isValid;
        this.errorMessage = valid.isValid ? null : (valid.errorMessage ?? '');
      });
    }
  }

  /**
   * Gets a display label from the config (for placeholder, empty message, etc.)
   * Uses the singular form ('=1') if available, otherwise the string value
   */
  private _getDisplayLabel(
    label: string | BdUiSelectLabelPluralization
  ): string {
    if (typeof label === 'string') {
      return label;
    }
    return label['=1'];
  }

  /**
   * Gets the appropriate label based on selection count
   */
  private _getLabelForCount(count: number): string {
    if (typeof this._selectLabelConfig === 'string') {
      return this._selectLabelConfig;
    }

    if (count === 0 && this._selectLabelConfig['=0']) {
      return this._selectLabelConfig['=0'];
    }
    if (count === 1) {
      return this._selectLabelConfig['=1'];
    }
    return this._selectLabelConfig.other;
  }

  protected _updateSelectedItemsLabel(count: number): void {
    const label = this._getLabelForCount(count);
    this.selectedItemsLabel = `{0} ${label} ausgewählt`;
  }

  protected setSelection(
    selection: TWrapperOption[],
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
      emitModelToViewChange?: boolean;
      emitViewToModelChange?: boolean;
    },
  ): void {
    this.selectCtrl.setValue(selection, options);
  }

  protected abstract onSelectionChange(selection: TWrapperOption[]): void;

  protected abstract emptySelection(): TWrapperOption[];

  protected abstract transformToWrapper(option: TOption): TWrapperOption;
}
